import { TRASH_DIR, type FileEntry, type SyncFs } from './reconcile';

/**
 * The sync's file system, on a real folder via the File System Access API
 * (Chromium desktop), or on the origin-private file system, which offers the
 * same handles — that is how the end-to-end tests drive it.
 */

type Entries = AsyncIterable<[string, FileSystemHandle]>;

function segments(path: string): { dirs: string[]; name: string } {
  const parts = path.split('/').filter(Boolean);
  const name = parts.pop() ?? '';
  return { dirs: parts, name };
}

async function directory(
  root: FileSystemDirectoryHandle,
  dirs: string[],
  create: boolean,
): Promise<FileSystemDirectoryHandle> {
  let dir = root;
  for (const part of dirs) dir = await dir.getDirectoryHandle(part, { create });
  return dir;
}

function timestamp(date = new Date()): string {
  return date.toISOString().replace(/[:.]/g, '-');
}

export function directoryFs(root: FileSystemDirectoryHandle): SyncFs {
  const file = async (path: string) => {
    const { dirs, name } = segments(path);
    return (await directory(root, dirs, false)).getFileHandle(name);
  };

  const write = async (path: string, text: string): Promise<number> => {
    const { dirs, name } = segments(path);
    const handle = await (await directory(root, dirs, true)).getFileHandle(name, { create: true });
    const writable = await handle.createWritable();
    await writable.write(text);
    await writable.close();
    return (await handle.getFile()).lastModified;
  };

  const remove = async (path: string) => {
    const { dirs, name } = segments(path);
    await (await directory(root, dirs, false)).removeEntry(name);
  };

  return {
    async list() {
      const out: FileEntry[] = [];
      const walk = async (dir: FileSystemDirectoryHandle, prefix: string) => {
        for await (const [name, handle] of (dir as unknown as { entries(): Entries }).entries()) {
          // Hidden folders (.git, .obsidian, our own trash) are not notes, and
          // neither are the READMEs of a project's installed dependencies.
          if (name.startsWith('.') || name === 'node_modules') continue;
          const path = prefix ? `${prefix}/${name}` : name;
          if (handle.kind === 'directory') {
            await walk(handle as FileSystemDirectoryHandle, path);
          } else if (/\.md$/i.test(name)) {
            const f = await (handle as FileSystemFileHandle).getFile();
            out.push({ path, lastModified: f.lastModified });
          }
        }
      };
      await walk(root, '');
      return out;
    },
    async read(path) {
      return (await (await file(path)).getFile()).text();
    },
    write,
    async trash(path) {
      // Copy first, delete second: if anything fails in between, the note exists twice, not zero times.
      const text = await (await (await file(path)).getFile()).text();
      await write(`${TRASH_DIR}/${timestamp()}/${path}`, text);
      await remove(path);
    },
    remove,
  };
}
